import React, { useMemo, useState, useEffect } from 'react';
import { Table, Input, Select, Typography, Button, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DownloadOutlined } from '@ant-design/icons';
import { makeStyles, useTheme, useMediaQuery } from '@material-ui/core';
import { adminCard, adminColors } from './adminStyles';

const { Option } = Select;
const { Text } = Typography;

const SHIRT_SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

const shirtSizeRank = (size) => {
  const rank = SHIRT_SIZE_ORDER.indexOf(size);
  return rank === -1 ? SHIRT_SIZE_ORDER.length : rank;
};

const useStyles = makeStyles((theme) => ({
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 12,
    marginBottom: 16,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 8,
      marginBottom: 12,
    },
  },
  stat: {
    ...adminCard,
    padding: '12px 16px',
    [theme.breakpoints.down('sm')]: {
      padding: '10px 12px',
    },
  },
  sizesStat: {
    gridColumn: 'span 2',
  },
  statLabel: {
    display: 'block',
    fontSize: 13,
  },
  statValue: {
    fontSize: 24,
    fontWeight: 600,
    lineHeight: 1.3,
    color: 'rgba(0, 0, 0, 0.85)',
  },
  statOf: {
    marginLeft: 6,
    fontSize: 14,
  },
  sizes: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '2px 16px',
    marginTop: 6,
  },
  sizeCount: {
    marginLeft: 4,
    fontWeight: 600,
    color: 'rgba(0, 0, 0, 0.85)',
  },
  listCard: {
    ...adminCard,
    overflow: 'hidden',
    '& .ant-table-pagination.ant-pagination': {
      margin: 16,
    },
  },
  controls: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: `1px solid ${adminColors.border}`,
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      padding: 12,
      borderBottom: 'none',
    },
  },
  searchInput: {
    width: 260,
    marginRight: 'auto',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginRight: 0,
    },
  },
  controlButton: {
    [theme.breakpoints.down('sm')]: {
      flex: 1,
    },
  },
  groupHeaderText: {
    fontSize: '14px',
    color: '#1890ff',
  },
  playerName: {
    paddingLeft: '20px',
  },
  groupHeaderRow: {
    '&:hover': {
      backgroundColor: '#f0f8ff !important',
    },
    backgroundColor: '#f0f8ff !important',
    border: '2px solid #1890ff',
  },
  playerRow: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  // Mobile-specific styles
  sortSelect: {
    width: '100%',
  },
  groupCard: {
    ...adminCard,
    marginTop: 12,
    overflow: 'hidden',
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 4px 6px 12px',
    backgroundColor: '#f0f8ff',
    borderBottom: `1px solid ${adminColors.border}`,
  },
  groupHeaderTitle: {
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-all',
    '&.ant-typography': {
      color: '#1890ff',
      fontWeight: 600,
    },
  },
  playerCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '10px 12px',
    '& + &': {
      borderTop: `1px solid ${adminColors.border}`,
    },
  },
  playerDetails: {
    flex: 1,
    minWidth: 0,
    '& .ant-typography': {
      display: 'block',
      wordBreak: 'break-all',
    },
  },
  paymentId: {
    fontSize: 12,
  },
}));

// Every player in a group shares one hole assignment.
const groupHole = (groupPlayers) => groupPlayers.find(player => player.holeAssignment)?.holeAssignment;

function HoleTag({ slot }) {
  if (!slot) return null;
  return <Tag color="blue" style={{ marginLeft: 8 }}>Hole {slot}</Tag>;
}

export default function PlayerTable({ players, onAddGroup, onEditGroup, onExport }) {
  const styles = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const filtered = useMemo(() => {
    // If no search term, return all players
    if (!search) {
      return players;
    }

    // Find groups that have at least one player matching the search
    const matchingGroups = new Set();
    players.forEach(player => {
      if (player.name.toLowerCase().includes(search.toLowerCase()) || 
          player.email.toLowerCase().includes(search.toLowerCase())) {
        matchingGroups.add(player.groupId);
      }
    });

    // Return all players from groups that have at least one match
    return players.filter(p => matchingGroups.has(p.groupId));
  }, [players, search]);

  const shirtSizeTotals = useMemo(() => {
    const totals = {};
    filtered.forEach(player => {
      const size = player.shirtSize || 'Unknown';
      totals[size] = (totals[size] || 0) + 1;
    });
    return Object.entries(totals).sort(([a], [b]) => shirtSizeRank(a) - shirtSizeRank(b) || a.localeCompare(b));
  }, [filtered]);

  const countGroups = (list) => new Set(list.map(player => player.groupId)).size;
  const groupCount = useMemo(() => countGroups(filtered), [filtered]);
  const totalGroupCount = useMemo(() => countGroups(players), [players]);

  // Group players by groupId and apply sorting within groups
  const groupedData = useMemo(() => {
    const groups = {};
    
    // Group filtered players
    filtered.forEach(player => {
      const groupId = player.groupId;
      if (!groups[groupId]) {
        groups[groupId] = [];
      }
      groups[groupId].push(player);
    });

    // Sort players within each group if sorting is applied
    if (sortField && sortOrder) {
      Object.keys(groups).forEach(groupId => {
        groups[groupId].sort((a, b) => {
          const aVal = a[sortField] || '';
          const bVal = b[sortField] || '';
          const comparison = aVal.localeCompare(bVal);
          return sortOrder === 'ascend' ? comparison : -comparison;
        });
      });
    }

    // For mobile, return grouped structure
    if (isMobile) {
      return Object.keys(groups).sort().map(groupId => ({
        groupId,
        players: groups[groupId],
        playerCount: groups[groupId].length,
        holeAssignment: groupHole(groups[groupId])
      }));
    }

    // For desktop, convert to table data structure with group headers
    const tableData = [];
    Object.keys(groups).sort().forEach(groupId => {
      const groupPlayers = groups[groupId];
      
      // Add group header row
      tableData.push({
        key: `group-${groupId}`,
        isGroupHeader: true,
        groupId: groupId,
        playerCount: groupPlayers.length,
        holeAssignment: groupHole(groupPlayers),
        name: `Group ${groupId}`,
        email: '',
        shirtSize: '',
        paymentId: ''
      });

      // Add player rows
      groupPlayers.forEach(player => {
        tableData.push({
          ...player,
          key: player._id,
          isGroupHeader: false,
          parentGroup: groupId
        });
      });
    });

    return tableData;
  }, [filtered, sortField, sortOrder, isMobile]);

  const columns = [
    { 
      title: 'Name', 
      dataIndex: 'name', 
      sorter: true,
      render: (text, record) => {
        if (record.isGroupHeader) {
          return {
            children: (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                  <Text strong className={styles.groupHeaderText}>
                    {text}
                  </Text>
                  <HoleTag slot={record.holeAssignment} />
                </span>
                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => onEditGroup(record.groupId)}
                >
                  Edit
                </Button>
              </div>
            ),
            props: {
              colSpan: 4,
            },
          };
        }
        return <span className={styles.playerName}>{text}</span>;
      }
    },
    { 
      title: 'Email', 
      dataIndex: 'email', 
      sorter: true,
      render: (text, record) => {
        if (record.isGroupHeader) {
          return {
            props: {
              colSpan: 0,
            },
          };
        }
        return text;
      }
    },
    { 
      title: 'Shirt size',
      dataIndex: 'shirtSize', 
      sorter: true,
      render: (text, record) => {
        if (record.isGroupHeader) {
          return {
            props: {
              colSpan: 0,
            },
          };
        }
        return text;
      }
    },
    { 
      title: 'Payment ID', 
      dataIndex: 'paymentId',
      render: (text, record) => {
        if (record.isGroupHeader) {
          return {
            props: {
              colSpan: 0,
            },
          };
        }
        return text;
      }
    }
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    setSortField(sorter.field || '');
    setSortOrder(sorter.order || '');
  };

  const sortOptions = [
    { label: 'Name A-Z', value: 'name-ascend' },
    { label: 'Name Z-A', value: 'name-descend' },
    { label: 'Email A-Z', value: 'email-ascend' },
    { label: 'Email Z-A', value: 'email-descend' },
    { label: 'Shirt size', value: 'shirtSize-ascend' },
  ];

  const handleSortChange = (value) => {
    if (!value) {
      setSortField('');
      setSortOrder('');
      return;
    }
    const [field, order] = value.split('-');
    setSortField(field);
    setSortOrder(order);
  };

  const renderMobileView = () => (
    groupedData.map(({ groupId, players, holeAssignment }) => (
      <div key={groupId} className={styles.groupCard}>
        <div className={styles.groupHeader}>
          <Text className={styles.groupHeaderTitle}>Group {groupId}</Text>
          <HoleTag slot={holeAssignment} />
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEditGroup(groupId)}
          >
            Edit
          </Button>
        </div>
        {players.map(player => (
          <div key={player._id} className={styles.playerCard}>
            <div className={styles.playerDetails}>
              <Text strong>{player.name}</Text>
              <Text type="secondary">{player.email}</Text>
              {player.paymentId && (
                <Text type="secondary" className={styles.paymentId}>Payment ID: {player.paymentId}</Text>
              )}
            </div>
            <Tag>{player.shirtSize || 'N/A'}</Tag>
          </div>
        ))}
      </div>
    ))
  );

  // While searching, counts show how much of the full roster matched.
  const renderOutOf = (total) => search && (
    <Text type="secondary" className={styles.statOf}>of {total}</Text>
  );

  return (
    <div>
      <div className={styles.stats}>
        <div className={styles.stat}>
          <Text type="secondary" className={styles.statLabel}>Players</Text>
          <span className={styles.statValue}>{filtered.length}</span>
          {renderOutOf(players.length)}
        </div>
        <div className={styles.stat}>
          <Text type="secondary" className={styles.statLabel}>Groups</Text>
          <span className={styles.statValue}>{groupCount}</span>
          {renderOutOf(totalGroupCount)}
        </div>
        <div className={`${styles.stat} ${styles.sizesStat}`}>
          <Text type="secondary" className={styles.statLabel}>Shirt sizes</Text>
          <div className={styles.sizes}>
            {shirtSizeTotals.map(([size, count]) => (
              <Text key={size}>
                {size}<span className={styles.sizeCount}>{count}</span>
              </Text>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.listCard}>
        <div className={styles.controls}>
          <Input.Search
            placeholder="Search name or email"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={styles.searchInput}
          />
          {isMobile && (
            <Select
              allowClear
              placeholder="Sort by"
              value={sortField && sortOrder ? `${sortField}-${sortOrder}` : undefined}
              onChange={handleSortChange}
              className={styles.sortSelect}
            >
              {sortOptions.map(opt => <Option key={opt.value} value={opt.value}>{opt.label}</Option>)}
            </Select>
          )}
          <Button
            icon={<DownloadOutlined />}
            onClick={onExport}
            disabled={!players.length}
            className={styles.controlButton}
          >
            Export CSV
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAddGroup}
            className={styles.controlButton}
          >
            Add player group
          </Button>
        </div>
        {!isMobile && (
          <Table
            columns={columns}
            dataSource={groupedData}
            rowKey="key"
            pagination={{ pageSize: 50 }}
            onChange={handleTableChange}
            rowClassName={(record) => record.isGroupHeader ? styles.groupHeaderRow : styles.playerRow}
            scroll={{ x: 600 }}
          />
        )}
      </div>
      {isMobile && renderMobileView()}
    </div>
  );
}
